import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/store/auth";
import { errorMessage } from "@/api/client";

// Provider credentials come from .env (VITE_*). If a value is missing, that
// button shows a friendly "not configured" notice instead of failing silently.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const FACEBOOK_APP_ID = import.meta.env.VITE_FACEBOOK_APP_ID;

export default function SocialButtons() {
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { loginWithGoogle, loginWithFacebook } = useAuth();
  const navigate = useNavigate();

  // Load the Facebook JS SDK once, if configured.
  useEffect(() => {
    if (!FACEBOOK_APP_ID || document.getElementById("facebook-jssdk")) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).fbAsyncInit = function () {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).FB.init({
        appId: FACEBOOK_APP_ID,
        cookie: true,
        xfbml: false,
        version: "v19.0",
      });
    };
    const js = document.createElement("script");
    js.id = "facebook-jssdk";
    js.src = "https://connect.facebook.net/en_US/sdk.js";
    js.async = true;
    js.defer = true;
    document.body.appendChild(js);
  }, []);

  // Exchange a provider token for our session, then go to the dashboard.
  const finish = async (fn: () => Promise<void>) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      navigate("/home", { replace: true });
    } catch (err) {
      setNotice(errorMessage(err, "Sign-in failed. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = () => {
    if (!GOOGLE_CLIENT_ID) {
      setNotice("Google sign-in isn't configured yet (set VITE_GOOGLE_CLIENT_ID).");
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const google = (window as any).google;
    if (!google?.accounts?.oauth2) {
      setNotice("Google is still loading — please try again in a moment.");
      return;
    }
    const client = google.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: "openid email profile",
      callback: (resp: { access_token?: string }) => {
        if (!resp.access_token) {
          setNotice("Google sign-in was cancelled.");
          return;
        }
        void finish(() => loginWithGoogle(resp.access_token as string));
      },
    });
    client.requestAccessToken();
  };

  const handleFacebook = () => {
    if (!FACEBOOK_APP_ID) {
      setNotice("Facebook sign-in isn't configured yet (set VITE_FACEBOOK_APP_ID).");
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const FB = (window as any).FB;
    if (!FB) {
      setNotice("Facebook is still loading — please try again in a moment.");
      return;
    }
    FB.login(
      (resp: { authResponse?: { accessToken?: string } }) => {
        const token = resp?.authResponse?.accessToken;
        if (!token) {
          setNotice("Facebook sign-in was cancelled.");
          return;
        }
        void finish(() => loginWithFacebook(token));
      },
      { scope: "email" },
    );
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-3">
        <SocialBtn label="Google" disabled={busy} onClick={handleGoogle}>
          <GoogleIcon />
        </SocialBtn>
        <SocialBtn label="Facebook" disabled={busy} onClick={handleFacebook}>
          <FacebookIcon />
        </SocialBtn>
      </div>
      {notice && (
        <p className="mt-3 rounded-lg bg-cream px-3 py-2 text-center text-xs text-slate-500">
          {notice}
        </p>
      )}
    </div>
  );
}

function SocialBtn({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`Continue with ${label}`}
      className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 py-2.5 text-sm font-medium text-navy-800 transition hover:bg-slate-50 disabled:opacity-60"
    >
      {children}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#1877F2" d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.69.24 2.69.24v2.95h-1.51c-1.49 0-1.96.93-1.96 1.87V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z" />
    </svg>
  );
}


